/**
 * Full-screen search overlay. Calls /api/live-scores/search which returns
 * { q, teams: [...], leagues: [...] }.
 *
 * Triggered by the magnifying-glass icon in the Scores header.
 * Selecting a team → navigates back to the matches list and (in future)
 * could filter to that team. For now we just close the overlay — the
 * favorite-star on each match row gives the user a way to pin them.
 */
import { useEffect, useState, useRef } from 'react';
import { useScoresNavigate } from './scoresPath';
import { X, Search, Loader2, Clock } from 'lucide-react';
import { useFavoriteTeams } from './useScoresFavorites';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recent team searches — remembered locally for one-tap access next time.
const RECENTS_KEY = 'sk_scores_recent_teams';
const RECENTS_MAX = 8;
const loadRecents = () => {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]'); } catch { return []; }
};
const pushRecent = (team) => {
  try {
    const list = loadRecents().filter((t) => t.team_id !== team.team_id);
    list.unshift({ team_id: team.team_id, team_name: team.team_name, crest: team.crest, league_name: team.league_name });
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
  } catch { /* ignore */ }
};

const ScoresSearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ teams: [], leagues: [] });
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const favTeams = useFavoriteTeams();
  const navigate = useScoresNavigate();

  useEffect(() => {
    if (open) {
      setQ('');
      setData({ teams: [], leagues: [] });
      setRecents(loadRecents());
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const goToTeam = (t) => {
    if (!t.team_id) return;
    pushRecent(t);
    onClose();
    navigate(`/team/${t.team_id}`);
  };

  const clearRecents = () => {
    try { localStorage.removeItem(RECENTS_KEY); } catch { /* ignore */ }
    setRecents([]);
  };

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) {
      setData({ teams: [], leagues: [] });
      return undefined;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/live-scores/search?q=${encodeURIComponent(q.trim())}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : { teams: [], leagues: [] }))
        .then((j) => setData({ teams: j.teams || [], leagues: j.leagues || [] }))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  if (!open) return null;

  const hasResults = data.teams.length > 0 || data.leagues.length > 0;

  return (
    <div className="fixed inset-0 z-[210] bg-[var(--sk-bg)]" data-testid="scores-search-overlay">
      <div className="max-w-lg mx-auto h-full flex flex-col">
        <div className="flex items-center gap-2 px-3 py-3 border-b border-[var(--sk-border)]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search teams or leagues…"
              className="w-full bg-[var(--sk-surface)] border border-[var(--sk-border)] rounded-xl pl-9 pr-3 py-2.5 text-[16px] text-white placeholder:text-zinc-500 outline-none focus:border-accent"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center"
            aria-label="Close search"
            data-testid="search-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {/* Recent searches (shown when not actively searching) */}
          {!loading && q.trim().length < 2 && recents.length > 0 && (
            <section className="mb-5" data-testid="search-recents">
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-[10px] font-black uppercase tracking-wider text-accent flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> Recent Searches
                </div>
                <button onClick={clearRecents} className="text-[10px] text-zinc-500 hover:text-zinc-300" data-testid="search-recents-clear">Clear</button>
              </div>
              <div className="space-y-1">
                {recents.map((t) => (
                  <button
                    key={t.team_id}
                    onClick={() => goToTeam(t)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[var(--sk-surface)] hover:bg-white/[0.04] text-left"
                    data-testid={`search-recent-${t.team_id}`}
                  >
                    {t.crest ? <img src={t.crest} alt="" className="w-6 h-6 object-contain" /> : <div className="w-6 h-6 rounded bg-white/5" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-white truncate">{t.team_name}</div>
                      {t.league_name && <div className="text-[10px] text-zinc-500 truncate">{t.league_name}</div>}
                    </div>
                    <Clock className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                  </button>
                ))}
              </div>
            </section>
          )}
          {loading && (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-accent" /></div>
          )}
          {!loading && q.trim().length >= 2 && !hasResults && (
            <div className="text-center text-sm text-zinc-500 py-12 px-6">
              No results for "{q.trim()}". Check the spelling and try the club's common name (e.g. "Inter", "Real Madrid").
            </div>
          )}
          {!loading && data.teams.length > 0 && (
            <section className="mb-5">
              <div className="text-[10px] font-black uppercase tracking-wider text-accent mb-2 px-1">Teams</div>
              <div className="space-y-1">
                {data.teams.map((t) => {
                  const isFav = favTeams.isFavorite(t.team_id);
                  return (
                    <div
                      key={t.team_id || t.team_name}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[var(--sk-surface)] hover:bg-white/[0.04]"
                      data-testid={`search-team-${t.team_id}`}
                    >
                      {t.crest ? (
                        <img src={t.crest} alt="" className="w-6 h-6 object-contain" />
                      ) : (
                        <div className="w-6 h-6 rounded bg-white/5" />
                      )}
                      <button
                        onClick={() => goToTeam(t)}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="text-[13px] text-white truncate">{t.team_name}</div>
                        <div className="text-[10px] text-zinc-500 truncate">{t.league_name}</div>
                      </button>
                      <button
                        onClick={() => favTeams.toggle({ id: t.team_id, name: t.team_name, logo: t.crest })}
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${isFav ? 'bg-accent-soft text-accent' : 'bg-white/5 text-zinc-400'}`}
                      >
                        {isFav ? '★ Favorited' : '☆ Favorite'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {!loading && data.leagues.length > 0 && (
            <section>
              <div className="text-[10px] font-black uppercase tracking-wider text-accent mb-2 px-1">Leagues</div>
              <div className="space-y-1">
                {data.leagues.map((lg) => (
                  <div
                    key={lg.league_id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[var(--sk-surface)]"
                    data-testid={`search-league-${lg.league_id}`}
                  >
                    {lg.league_logo ? (
                      <img src={lg.league_logo} alt="" className="w-6 h-6 object-contain" />
                    ) : (
                      <div className="w-6 h-6 rounded bg-white/5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-white truncate">{lg.league_name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{lg.country}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoresSearchOverlay;
